/**
 * Theme-Auswahl — dieselben Themes, die auch die Settings-App anbietet (#504).
 *
 * Warum nicht einfach hell/dunkel: Das Aussehen entsteht nicht aus der
 * Theme-Klasse, sondern aus den CSS-Variablen, die der `SettingsContext` aus
 * dem gewählten Theme schreibt (Akzent, Hintergrund, Textfarben …). Wer nur
 * `setTheme('dark')` ruft, tauscht die Klasse und sieht nichts passieren — die
 * Variablen des zuvor gewählten Themes gelten weiter.
 *
 * Welche Themes zur Wahl stehen, entscheidet das Backend: `GET /themes` filtert
 * nach dem JWT, also nach Rolle und Mandant. Die Standalone-Ansicht zeigt damit
 * automatisch dieselben Mandanten-Designs wie der Desktop, ohne eigene Regel.
 *
 * Die Settings-App (`SettingsApp.tsx`) hat diese Schritte bislang inline; sie
 * ließe sich später auf diesen Hook umstellen, ohne dass sich ihr Verhalten
 * ändert.
 */
import { useCallback, useEffect, useState } from 'react'
import { loadThemeOptionsForUser, type ThemeOption } from '@/constants/theme-options'
import { themeService } from '@/services/theme-service'
import { authService } from '@/services/auth-service'
import { useSettingsContext } from '@/contexts/SettingsContext'
import { useTheme } from '@/hooks/useTheme'
import { logError } from '@/lib/logger'

export function useThemeSelection() {
  const { settings, saveSettings } = useSettingsContext()
  const { setTheme } = useTheme()
  const [options, setOptions] = useState<ThemeOption[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isApplying, setIsApplying] = useState(false)

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const user = await authService.getCurrentUser().catch(() => null)
        if (!user) return
        const loaded = await loadThemeOptionsForUser(user.id)
        if (active) setOptions(loaded)
      } catch (error) {
        logError(error as Error, { context: 'useThemeSelection', action: 'load' })
      } finally {
        if (active) setIsLoading(false)
      }
    })()
    return () => {
      active = false
    }
  }, [])

  // Themes können im Cortex angelegt/geändert werden — dasselbe Ereignis, auf
  // das auch die Settings-App hört.
  useEffect(() => {
    const reload = async () => {
      const user = await authService.getCurrentUser().catch(() => null)
      if (!user) return
      setOptions(await loadThemeOptionsForUser(user.id))
    }
    const handler = () => void reload()
    window.addEventListener('theme-updated', handler)
    return () => window.removeEventListener('theme-updated', handler)
  }, [])

  const selectTheme = useCallback(
    async (themeId: string) => {
      setIsApplying(true)
      try {
        const theme = await themeService.getThemeById(themeId)
        if (!theme) return
        // Reihenfolge wie in der Settings-App: erst die Klasse, dann speichern —
        // der SettingsContext zieht sich die CSS-Variablen über `theme_id`.
        setTheme(theme.css_class)
        await saveSettings({
          ...settings,
          theme_id: theme.id,
          darkMode: theme.is_dark_mode,
          background: {
            image: theme.background_image_url || settings.background.image,
            imageMode: 'cover' as const,
          },
        })
      } catch (error) {
        logError(error as Error, { context: 'useThemeSelection', action: 'select', themeId })
      } finally {
        setIsApplying(false)
      }
    },
    [settings, saveSettings, setTheme],
  )

  return {
    options,
    isLoading,
    isApplying,
    selectedId: settings.theme_id,
    selectTheme,
  }
}
